import type { PanelPlugin, PluginContext } from '../core/plugin'
import type { MessageBridge } from './message-bridge'
import type { HistoryManager, StyleChange } from './history'
import { CONFIG, type FeatureConfig, hasAnyStyleFeature } from './config'
import { ICONS } from './icons'
import {
  log,
  getInlineStyleValue,
  getStyleValueForRecord,
  setInlineStyleImportant,
} from './utils'

interface AITextResultPayload {
  success?: boolean,
  content?: string,
  elementId?: string,
}

export class ToolbarManager implements PanelPlugin {
  readonly name = 'toolbar'

  private bridge: MessageBridge

  private history: HistoryManager

  private featureConfig: FeatureConfig

  private container: HTMLDivElement | null = null

  private activeElement: HTMLElement | null = null

  private colorPickerType: 'text' | 'bg' | null = null

  private isVisible = false

  private _scrollHandler: (() => void) | null = null

  private _resizeObserver: ResizeObserver | null = null

  private _showTextResult: ((content: string) => void) | null = null

  private _hideAIPreview: (() => void) | null = null

  private _applyAIText: ((content: string | null) => void) | null = null

  private _regenerateText: (() => void) | null = null

  private _pendingAIContent: string | null = null

  // AI 润色"等待中的目标 elementId"。发请求时记录；切换/隐藏面板时清空。
  // 结果到达时若为 null 或与当前不匹配，则丢弃，避免错位替换。
  private _pendingAiTargetElementId: string | null = null

  private _initialInlineStyles: Record<string, string> | null = null

  get visible(): boolean {
    return this.isVisible
  }

  constructor(messageBridge: MessageBridge, historyManager: HistoryManager, featureConfig: FeatureConfig) {
    this.bridge = messageBridge
    this.history = historyManager
    this.featureConfig = featureConfig
  }

  create(_ctx?: PluginContext): void {
    if (this.container) return
    if (!hasAnyStyleFeature(this.featureConfig)) {
      log('没有启用的样式功能，不创建工具栏')
      return
    }

    const el = document.createElement('div')
    el.setAttribute(CONFIG.TOOLBAR_ATTR, 'true')
    el.className = 'musk-style-toolbar'

    const groups: string[] = []

    if (this.featureConfig.fontSize) {
      groups.push([
        '<div class="mst-group">',
        '  <div class="mst-fontsize-wrap">',
        '    <button class="mst-btn mst-fontsize-btn" data-action="fontSize" data-tooltip="字体大小">',
        `      ${ICONS.FONT_SIZE}`,
        '      <span class="mst-fontsize-value">16</span>',
        `      ${ICONS.CHEVRON_DOWN}`,
        '    </button>',
        '    <div class="mst-fontsize-dropdown">',
        CONFIG.FONT_SIZES.map((s) => `      <div class="mst-fontsize-option" data-size="${s}"><span class="mst-option-check">${ICONS.CHECK}</span><span class="mst-option-label">${s}</span></div>`).join('\n'),
        '    </div>',
        '  </div>',
        '</div>',
      ].join('\n'))
    }

    const colorBtns: string[] = []
    if (this.featureConfig.textColor) {
      colorBtns.push([
        '  <div class="mst-color-wrap">',
        '    <button class="mst-btn mst-color-btn" data-action="textColor" data-tooltip="字体颜色">',
        '      <span class="mst-color-swatch mst-text-color-indicator" style="background:#000000"></span>',
        `      ${ICONS.CHEVRON_DOWN}`,
        '    </button>',
        '    <div class="mst-color-dropdown" data-color-type="text">',
        '      <div class="mst-color-grid">',
        CONFIG.COLORS.map((c) => `        <div class="mst-color-item" data-color="${c}" style="background:${c}" title="${c}"></div>`).join('\n'),
        '      </div>',
        '    </div>',
        '  </div>',
      ].join('\n'))
    }
    if (this.featureConfig.bgColor) {
      colorBtns.push([
        '  <div class="mst-color-wrap">',
        '    <button class="mst-btn mst-color-btn" data-action="bgColor" title="背景颜色">',
        '      <span class="mst-color-swatch mst-bg-color-indicator" style="background:transparent;border:1px dashed #ccc"></span>',
        `      ${ICONS.CHEVRON_DOWN}`,
        '    </button>',
        '    <div class="mst-color-dropdown" data-color-type="bg">',
        '      <div class="mst-color-grid">',
        '        <div class="mst-color-item mst-color-none" data-color="transparent" title="无背景">✕</div>',
        CONFIG.COLORS.map((c) => `        <div class="mst-color-item" data-color="${c}" style="background:${c}" title="${c}"></div>`).join('\n'),
        '      </div>',
        '    </div>',
        '  </div>',
      ].join('\n'))
    }
    if (colorBtns.length > 0) {
      groups.push(`<div class="mst-group">\n${colorBtns.join('\n')}\n</div>`)
    }

    if (this.featureConfig.textAlign) {
      groups.push([
        '<div class="mst-group">',
        '  <div class="mst-align-wrap">',
        '    <button class="mst-btn mst-align-trigger" data-tooltip="对齐">',
        `      ${ICONS.ALIGN_LEFT}`,
        `      ${ICONS.CHEVRON_DOWN}`,
        '    </button>',
        '    <div class="mst-align-dropdown">',
        `      <div class="mst-align-option" data-align="left"><span class="mst-option-check">${ICONS.CHECK}</span>${ICONS.ALIGN_LEFT}</div>`,
        `      <div class="mst-align-option" data-align="center"><span class="mst-option-check">${ICONS.CHECK}</span>${ICONS.ALIGN_CENTER}</div>`,
        `      <div class="mst-align-option" data-align="right"><span class="mst-option-check">${ICONS.CHECK}</span>${ICONS.ALIGN_RIGHT}</div>`,
        `      <div class="mst-align-option" data-align="justify"><span class="mst-option-check">${ICONS.CHECK}</span>${ICONS.ALIGN_JUSTIFY}</div>`,
        '    </div>',
        '  </div>',
        '</div>',
      ].join('\n'))
    }

    const formatBtns: string[] = []
    if (this.featureConfig.bold) {
      formatBtns.push(
        '  <button class="mst-btn" data-action="bold" data-tooltip="加粗">',
        `    ${ICONS.BOLD}`,
        '  </button>',
      )
    }
    if (this.featureConfig.italic) {
      formatBtns.push(
        '  <button class="mst-btn" data-action="italic" data-tooltip="斜体">',
        `    ${ICONS.ITALIC}`,
        '  </button>',
      )
    }
    if (this.featureConfig.underline) {
      formatBtns.push(
        '  <button class="mst-btn" data-action="underline" data-tooltip="下划线">',
        `    ${ICONS.UNDERLINE}`,
        '  </button>',
      )
    }
    if (formatBtns.length > 0) {
      groups.push(`<div class="mst-group">\n${formatBtns.join('\n')}\n</div>`)
    }

    const toolbarCard = document.createElement('div')
    toolbarCard.className = 'mst-card mst-toolbar-card'

    const buttonsRow = document.createElement('div')
    buttonsRow.className = 'mst-buttons-row'
    buttonsRow.innerHTML = groups.join('\n<div class="mst-divider"></div>\n')

    const aiSectionHtml = [
      '<div class="mst-divider"></div>',
      '<div class="mst-ai-section">',
      `  <span class="musk-ai-label">${ICONS.AI_SPARKLE}</span>`,
      '  <input type="text" class="musk-ai-input" placeholder="描述如何修改文本...">',
      `  <button class="musk-ai-send" disabled>${ICONS.SEND_ARROW}</button>`,
      '</div>',
    ].join('\n')
    buttonsRow.insertAdjacentHTML('beforeend', aiSectionHtml)
    toolbarCard.appendChild(buttonsRow)
    el.appendChild(toolbarCard)

    const resultCard = document.createElement('div')
    resultCard.className = 'mst-card mst-result-card'
    resultCard.style.display = 'none'
    resultCard.innerHTML = [
      '<div class="musk-ai-preview"></div>',
      '<div class="musk-ai-preview-actions">',
      `  <button class="musk-ai-regenerate">${ICONS.RETRY}<span>重试</span></button>`,
      `  <button class="musk-ai-apply">${ICONS.REPLACE}<span>替换</span></button>`,
      `  <button class="musk-ai-discard">${ICONS.TRASH}<span>弃用</span></button>`,
      '</div>',
    ].join('\n')
    el.appendChild(resultCard)

    document.body.appendChild(el)
    this.container = el
    this._bindEvents()
    this._setupScrollHandler()

    log('样式工具栏已创建', { features: this.featureConfig })
  }

  private _bindEvents(): void {
    if (!this.container) return
    const self = this

    this.container.addEventListener('mousedown', (e: MouseEvent) => {
      if ((e.target as Element)?.closest?.('.musk-ai-input')) { e.stopPropagation(); return }
      e.preventDefault()
      e.stopPropagation()
    }, true)

    // AI 润色输入
    const aiInput = this.container.querySelector('.musk-ai-input') as HTMLInputElement | null
    const aiSend = this.container.querySelector('.musk-ai-send') as HTMLButtonElement | null
    const aiPreview = this.container.querySelector('.musk-ai-preview') as HTMLElement | null
    const resultCard = this.container.querySelector('.mst-result-card') as HTMLElement | null
    let _lastTextPrompt = ''

    const showTextResult = function (content: string): void {
      if (!aiPreview || !resultCard) return
      aiPreview.textContent = content
      resultCard.style.display = 'block'
      self._updatePosition()
    }
    self._showTextResult = showTextResult

    self._hideAIPreview = function (): void {
      if (aiPreview) aiPreview.textContent = ''
      if (resultCard) resultCard.style.display = 'none'
      self._updatePosition()
    }

    const clearAiInput = function (): void {
      if (aiInput) { aiInput.value = ''; aiInput.disabled = false }
      if (aiSend) { aiSend.disabled = true; aiSend.classList.remove('musk-ai-loading') }
      _lastTextPrompt = ''
    }

    const doTextAiSend = function (): void {
      const text = aiInput?.value.trim()
      if (!text || !self.activeElement) return
      const elementId = self.activeElement.getAttribute('data-musk-element-id') || ''
      _lastTextPrompt = text
      if (aiSend) aiSend.disabled = true
      if (aiInput) aiInput.disabled = true
      aiSend?.classList.add('musk-ai-loading')
      self._hideAIPreview?.()
      self._pendingAiTargetElementId = elementId
      self.bridge.send('AI_POLISH_TEXT', {
        mode: 'text',
        elementId,
        content: self.activeElement.innerText || '',
        prompt: text,
      })
      log('AI 润色请求已发送', { mode: 'text', elementId })
    }

    self._applyAIText = function (content: string | null): void {
      if (!self.activeElement || !content) return
      self.activeElement.innerText = content
      self._hideAIPreview?.()
      log('AI 文字润色已应用')
    }

    self._regenerateText = function (): void {
      if (!_lastTextPrompt || !self.activeElement) return
      doTextAiSend()
    }

    self._pendingAIContent = null

    if (aiInput && aiSend) {
      aiSend.addEventListener('click', (e: MouseEvent) => { e.preventDefault(); e.stopPropagation(); doTextAiSend() })
      aiInput.addEventListener('keydown', (e: KeyboardEvent) => {
        e.stopPropagation()
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); doTextAiSend() }
      })
      aiInput.addEventListener('input', () => {
        aiSend.disabled = !aiInput.value.trim()
      })
    }

    this.container.addEventListener('click', (e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()

      if (!e.target) return
      const target = e.target as Element

      // AI 润色发送
      const aiSendTarget = target.closest('.musk-ai-send') as HTMLButtonElement | null
      if (aiSendTarget && !aiSendTarget.disabled) {
        doTextAiSend()
        return
      }

      // AI 预览动作：应用 / 放弃 / 重新生成
      if (target.closest('.musk-ai-apply')) {
        self._applyAIText?.(self._pendingAIContent)
        clearAiInput()
        return
      }
      if (target.closest('.musk-ai-discard')) {
        self._hideAIPreview?.()
        clearAiInput()
        return
      }
      if (target.closest('.musk-ai-regenerate')) {
        self._regenerateText?.()
        return
      }

      // Font size option
      const sizeOption = target.closest('.mst-fontsize-option')
      if (sizeOption) {
        const size = sizeOption.getAttribute('data-size')
        self._applyFontSize(`${size}px`)
        self._closeFontSizeDropdown()
        return
      }

      // Font size button toggle
      const fontSizeBtn = target.closest('.mst-fontsize-btn')
      if (fontSizeBtn) {
        self._toggleFontSizeDropdown()
        self._closeColorDropdowns()
        self._closeAlignDropdown()
        return
      }

      // Color item
      const colorItem = target.closest('.mst-color-item')
      if (colorItem) {
        const color = colorItem.getAttribute('data-color')
        const dropdown = colorItem.closest('.mst-color-dropdown')
        const colorType = dropdown?.getAttribute('data-color-type')
        if (colorType === 'text') {
          self._applyTextColor(color!)
        } else {
          self._applyBgColor(color!)
        }
        self._closeColorDropdowns()
        return
      }

      // Color button toggle
      const colorBtn = target.closest('.mst-color-btn')
      if (colorBtn) {
        const wrap = colorBtn.closest('.mst-color-wrap')
        const dd = wrap?.querySelector<HTMLElement>('.mst-color-dropdown')
        const isOpen = dd?.classList.contains('mst-open')
        self._closeColorDropdowns()
        self._closeFontSizeDropdown()
        self._closeAlignDropdown()
        if (!isOpen && dd) {
          self._openColorDropdown(dd)
        }
        return
      }

      // Alignment trigger toggle
      const alignTrigger = target.closest('.mst-align-trigger')
      if (alignTrigger) {
        self._toggleAlignDropdown()
        self._closeFontSizeDropdown()
        self._closeColorDropdowns()
        return
      }

      // Alignment option
      const alignOption = target.closest('.mst-align-option')
      if (alignOption) {
        const align = alignOption.getAttribute('data-align')
        if (align) self._applyTextAlign(align)
        self._closeAlignDropdown()
        return
      }

      const btn = target.closest('.mst-btn[data-action]')
      if (btn) {
        const actionName = btn.getAttribute('data-action')
        if (actionName === 'bold') self._applyBold()
        else if (actionName === 'italic') self._applyItalic()
        else if (actionName === 'underline') self._applyUnderline()
      }
    }, true)
  }

  private _setupScrollHandler(): void {
    const self = this
    this._scrollHandler = function (): void {
      if (self.isVisible && self.activeElement) {
        self._updatePosition()
      }
    }
    window.addEventListener('scroll', this._scrollHandler, { passive: true })
  }

  show(element: HTMLElement, _ctx?: PluginContext): void {
    if (!this.container) this.create()
    if (!this.container) return
    const newElementId = element.getAttribute('data-musk-element-id') || ''
    if (this._pendingAiTargetElementId && this._pendingAiTargetElementId !== newElementId) {
      log('Toolbar 切换到其他元素，作废 pending AI 请求', {
        was: this._pendingAiTargetElementId,
        now: newElementId,
      })
      this._pendingAiTargetElementId = null
    }
    this.activeElement = element
    this._snapshotInitialStyles(element)
    this._syncState()
    this.container.style.display = 'flex'
    this.isVisible = true
    this._updatePosition()
    this._closeAllDropdowns()
    this._hideAIPreview?.()
    this._pendingAIContent = null
    const aiInput = this.container.querySelector('.musk-ai-input') as HTMLInputElement | null
    const aiSendBtn = this.container.querySelector('.musk-ai-send') as HTMLButtonElement | null
    if (aiInput) { aiInput.value = ''; aiInput.disabled = false }
    if (aiSendBtn) { aiSendBtn.disabled = true; aiSendBtn.classList.remove('musk-ai-loading') }

    if (this._resizeObserver) this._resizeObserver.disconnect()
    this._resizeObserver = new ResizeObserver(() => {
      if (this.isVisible) this._updatePosition()
    })
    this._resizeObserver.observe(element)
  }

  private _snapshotInitialStyles(element: HTMLElement): void {
    const props = ['fontWeight', 'fontStyle', 'textDecoration', 'fontSize', 'color', 'backgroundColor', 'textAlign']
    this._initialInlineStyles = {}
    for (let i = 0; i < props.length; i++) {
      this._initialInlineStyles[props[i]] = getInlineStyleValue(element, props[i])
    }
  }

  getStyleDiff(element: HTMLElement): StyleChange[] {
    if (!this._initialInlineStyles) return []
    const changes: StyleChange[] = []
    const initial = this._initialInlineStyles
    const props = Object.keys(initial)
    for (let i = 0; i < props.length; i++) {
      const prop = props[i]
      const currentVal = getInlineStyleValue(element, prop)
      if (currentVal !== initial[prop]) {
        changes.push({ property: prop, oldValue: initial[prop], newValue: currentVal })
      }
    }
    return changes
  }

  clearEditSession(): void {
    this._initialInlineStyles = null
  }

  hide(): void {
    if (!this.container) return
    this.container.style.display = 'none'
    this.isVisible = false
    this._closeAllDropdowns()
    this._resetAILoading()
    this._hideAIPreview?.()
    this._pendingAIContent = null
    this._pendingAiTargetElementId = null
    if (this._resizeObserver) {
      this._resizeObserver.disconnect()
      this._resizeObserver = null
    }
    this.activeElement = null
  }

  private _resetAILoading(): void {
    if (!this.container) return
    const aiInput = this.container.querySelector('.musk-ai-input') as HTMLInputElement | null
    const aiSend = this.container.querySelector('.musk-ai-send') as HTMLButtonElement | null
    if (aiSend) aiSend.classList.remove('musk-ai-loading')
    if (aiInput) aiInput.disabled = false
    if (aiSend) aiSend.disabled = !(aiInput && aiInput.value.trim())
  }

  handleAITextResult(payload: AITextResultPayload | null): void {
    const expected = this._pendingAiTargetElementId
    this._pendingAiTargetElementId = null
    const currentElementId = this.activeElement?.getAttribute('data-musk-element-id') || null

    this._resetAILoading()

    if (!expected) {
      log('AI 文字润色结果已过期（面板已切换/关闭），丢弃')
      this._hideAIPreview?.()
      return
    }
    if (payload?.elementId && payload.elementId !== expected) {
      log('AI 文字润色结果 elementId 与请求不一致，丢弃', {
        expected,
        got: payload.elementId,
      })
      this._hideAIPreview?.()
      return
    }
    if (currentElementId !== expected) {
      log('AI 文字润色结果与当前 activeElement 不一致，丢弃', {
        expected,
        current: currentElementId,
      })
      this._hideAIPreview?.()
      return
    }
    if (!payload || !payload.success || !payload.content) {
      this._hideAIPreview?.()
      return
    }
    this._pendingAIContent = payload.content
    if (typeof this._showTextResult === 'function') {
      this._showTextResult(payload.content)
    }
    log('AI 文字润色结果已展示', { elementId: payload.elementId })
  }

  private _updatePosition(): void {
    if (!this.activeElement || !this.container) return
    const rect = this.activeElement.getBoundingClientRect()
    const toolbarH = this.container.offsetHeight
    const toolbarW = this.container.offsetWidth
    const gap = 8

    let top = rect.top - toolbarH - gap
    if (top < 4) {
      top = rect.bottom + gap
    }

    let left = rect.left + (rect.width - toolbarW) / 2
    if (left < 4) left = 4
    if (left + toolbarW > window.innerWidth - 4) {
      left = window.innerWidth - toolbarW - 4
    }

    this.container.style.top = `${top}px`
    this.container.style.left = `${left}px`
  }

  private _syncState(): void {
    if (!this.activeElement || !this.container) return
    const cs = window.getComputedStyle(this.activeElement)

    const currentSize = Math.round(parseFloat(cs.fontSize))
    const sizeLabel = this.container.querySelector('.mst-fontsize-value')
    if (sizeLabel) sizeLabel.textContent = String(currentSize)
    const fontOptions = this.container.querySelectorAll('.mst-fontsize-option')
    fontOptions.forEach((opt) => {
      const match = parseInt(opt.getAttribute('data-size') || '0') === currentSize
      opt.classList.toggle('mst-selected', match)
    })

    const boldBtn = this.container.querySelector('[data-action="bold"]')
    if (boldBtn) {
      const isBold = cs.fontWeight === '700' || cs.fontWeight === 'bold' || parseInt(cs.fontWeight) >= 700
      boldBtn.classList.toggle('mst-active', isBold)
    }

    const italicBtn = this.container.querySelector('[data-action="italic"]')
    if (italicBtn) italicBtn.classList.toggle('mst-active', cs.fontStyle === 'italic')

    const underlineBtn = this.container.querySelector('[data-action="underline"]')
    if (underlineBtn) underlineBtn.classList.toggle('mst-active', cs.textDecorationLine.indexOf('underline') !== -1)

    const tcIndicator = this.container.querySelector('.mst-text-color-indicator') as HTMLElement | null
    if (tcIndicator) tcIndicator.style.background = cs.color

    const bgIndicator = this.container.querySelector('.mst-bg-color-indicator') as HTMLElement | null
    if (bgIndicator) {
      const bg = cs.backgroundColor
      if (bg === 'rgba(0, 0, 0, 0)' || bg === 'transparent') {
        bgIndicator.style.background = 'transparent'
        bgIndicator.style.border = '1px dashed #ccc'
      } else {
        bgIndicator.style.background = bg
        bgIndicator.style.border = '1px solid rgba(0,0,0,0.06)'
      }
    }

    let curAlign = cs.textAlign
    if (curAlign === 'start') curAlign = 'left'
    else if (curAlign === 'end') curAlign = 'right'
    const alignOptions = this.container.querySelectorAll('.mst-align-option')
    alignOptions.forEach((opt) => {
      const match = opt.getAttribute('data-align') === curAlign
      opt.classList.toggle('mst-selected', match)
    })
    const alignIcon = this.container.querySelector('.mst-align-icon')
    if (alignIcon) {
      const alignIcons: Record<string, string> = {
        left: ICONS.ALIGN_LEFT,
        center: ICONS.ALIGN_CENTER,
        right: ICONS.ALIGN_RIGHT,
        justify: ICONS.ALIGN_JUSTIFY,
      }
      const nextAlignIcon = alignIcons[curAlign]
      const alignTrigger = this.container.querySelector('.mst-align-trigger')
      if (nextAlignIcon && alignTrigger?.getAttribute('data-current-align') !== curAlign) {
        alignIcon.outerHTML = nextAlignIcon
        alignTrigger?.setAttribute('data-current-align', curAlign)
      }
    }
  }

  private _applyFontSize(size: string): void {
    if (!this.activeElement) return
    const oldVal = getStyleValueForRecord(this.activeElement, 'fontSize')
    if (oldVal === size) return
    setInlineStyleImportant(this.activeElement, 'fontSize', size)
    this._recordStyleOp('fontSize', size, oldVal)
    this._syncState()
  }

  private _applyTextColor(color: string): void {
    if (!this.activeElement) return
    const oldVal = getStyleValueForRecord(this.activeElement, 'color')
    setInlineStyleImportant(this.activeElement, 'color', color)
    this._recordStyleOp('color', color, oldVal)
    this._syncState()
  }

  private _applyBgColor(color: string): void {
    if (!this.activeElement) return
    const oldVal = getStyleValueForRecord(this.activeElement, 'backgroundColor')
    const newVal = color === 'transparent' ? '' : color
    setInlineStyleImportant(this.activeElement, 'backgroundColor', newVal)
    this._recordStyleOp('backgroundColor', newVal, oldVal)
    this._syncState()
  }

  private _applyBold(): void {
    if (!this.activeElement) return
    const cs = window.getComputedStyle(this.activeElement)
    const isBold = cs.fontWeight === '700' || cs.fontWeight === 'bold' || parseInt(cs.fontWeight) >= 700
    const oldVal = getStyleValueForRecord(this.activeElement, 'fontWeight')
    const newVal = isBold ? 'normal' : 'bold'
    setInlineStyleImportant(this.activeElement, 'fontWeight', newVal)
    this._recordStyleOp('fontWeight', newVal, oldVal)
    this._syncState()
  }

  private _applyItalic(): void {
    if (!this.activeElement) return
    const cs = window.getComputedStyle(this.activeElement)
    const isItalic = cs.fontStyle === 'italic'
    const oldVal = getStyleValueForRecord(this.activeElement, 'fontStyle')
    const newVal = isItalic ? 'normal' : 'italic'
    setInlineStyleImportant(this.activeElement, 'fontStyle', newVal)
    this._recordStyleOp('fontStyle', newVal, oldVal)
    this._syncState()
  }

  private _applyUnderline(): void {
    if (!this.activeElement) return
    const cs = window.getComputedStyle(this.activeElement)
    const hasUnderline = cs.textDecorationLine.indexOf('underline') !== -1
    const oldVal = getStyleValueForRecord(this.activeElement, 'textDecoration')
    const newVal = hasUnderline ? 'none' : 'underline'
    setInlineStyleImportant(this.activeElement, 'textDecoration', newVal)
    this._recordStyleOp('textDecoration', newVal, oldVal)
    this._syncState()
  }

  private _applyTextAlign(align: string): void {
    if (!this.activeElement) return
    const oldVal = getStyleValueForRecord(this.activeElement, 'textAlign')
    if (oldVal === align) return
    setInlineStyleImportant(this.activeElement, 'textAlign', align)
    this._recordStyleOp('textAlign', align, oldVal)
    this._syncState()
  }

  private _recordStyleOp(property: string, value: string, oldValue: string): void {
    if (!this.activeElement) return
    const elementId = this.activeElement.getAttribute('data-musk-element-id')
    if (!elementId) return
    this.bridge.send('EDIT_END', {
      elementId,
      changed: true,
      styleChange: true,
      details: { property, value },
      oldDetails: { property, value: oldValue },
    })
    this.bridge.send('HAS_CHANGES_UPDATED', { hasChanges: true })
  }

  private _toggleFontSizeDropdown(): void {
    if (!this.container) return
    const dd = this.container.querySelector<HTMLElement>('.mst-fontsize-dropdown')
    if (!dd) return
    if (dd.classList.contains('mst-open')) {
      this._closeFontSizeDropdown()
      return
    }
    // 先打开并清掉上次状态，再根据实际可用空间决定朝上 / 朝下与 max-height
    dd.classList.remove('mst-drop-up')
    dd.style.maxHeight = ''
    dd.classList.add('mst-open')
    this._adjustDropdownPlacement(dd)
  }

  private _closeFontSizeDropdown(): void {
    if (!this.container) return
    const dd = this.container.querySelector<HTMLElement>('.mst-fontsize-dropdown')
    if (!dd) return
    dd.classList.remove('mst-open')
    dd.classList.remove('mst-drop-up')
    dd.style.maxHeight = ''
  }

  // 根据 wrap 在 viewport 中的位置 + dropdown 实际内容高度
  // 自动决定朝下还是朝上展开，并设置不会溢出 viewport 的 max-height
  private _adjustDropdownPlacement(dd: HTMLElement): void {
    const wrap = dd.parentElement
    if (!wrap) return
    const wrapRect = wrap.getBoundingClientRect()
    const viewportH = window.innerHeight
    const margin = 10 // 与 dropdown 自身的 margin-top/bottom 保持一致
    const safety = 8  // 留一点底部安全间距
    const spaceBelow = Math.max(0, viewportH - wrapRect.bottom - margin - safety)
    const spaceAbove = Math.max(0, wrapRect.top - margin - safety)
    const desiredH = dd.scrollHeight // dropdown 完整内容高度

    // 默认朝下；下方放不下且上方更宽敞时翻转
    let dropUp = false
    let availableH = spaceBelow
    if (spaceBelow < desiredH && spaceAbove > spaceBelow) {
      dropUp = true
      availableH = spaceAbove
    }

    if (dropUp) dd.classList.add('mst-drop-up')
    else dd.classList.remove('mst-drop-up')

    const cap = 320 // 与 CSS 默认 max-height 保持一致
    dd.style.maxHeight = `${Math.max(120, Math.min(cap, availableH))}px`
  }

  // 打开指定的 color dropdown，并根据 viewport 调整朝上/朝下与 max-height
  private _openColorDropdown(dd: HTMLElement): void {
    dd.classList.remove('mst-drop-up')
    dd.style.maxHeight = ''
    dd.classList.add('mst-open')
    this._adjustDropdownPlacement(dd)
  }

  private _closeColorDropdowns(): void {
    if (!this.container) return
    const dds = this.container.querySelectorAll<HTMLElement>('.mst-color-dropdown')
    dds.forEach((d) => {
      d.classList.remove('mst-open')
      d.classList.remove('mst-drop-up')
      d.style.maxHeight = ''
    })
  }

  private _toggleAlignDropdown(): void {
    if (!this.container) return
    const dd = this.container.querySelector<HTMLElement>('.mst-align-dropdown')
    if (!dd) return
    if (dd.classList.contains('mst-open')) {
      this._closeAlignDropdown()
      return
    }
    dd.classList.remove('mst-drop-up')
    dd.style.maxHeight = ''
    dd.classList.add('mst-open')
    this._adjustDropdownPlacement(dd)
  }

  private _closeAlignDropdown(): void {
    if (!this.container) return
    const dd = this.container.querySelector<HTMLElement>('.mst-align-dropdown')
    if (!dd) return
    dd.classList.remove('mst-open')
    dd.classList.remove('mst-drop-up')
    dd.style.maxHeight = ''
  }

  private _closeAllDropdowns(): void {
    this._closeFontSizeDropdown()
    this._closeColorDropdowns()
    this._closeAlignDropdown()
  }

  destroy(): void {
    if (this._scrollHandler) {
      window.removeEventListener('scroll', this._scrollHandler)
    }
    if (this._resizeObserver) {
      this._resizeObserver.disconnect()
    }
    if (this.container) {
      this.container.remove()
      this.container = null
    }
    this.isVisible = false
  }
}
